import { Button, Dropdown, Spin, Table } from "antd"
import React, { useRef } from "react"
import { useTranslation } from "react-i18next"
import { Plus as PlusIcon } from "react-feather"
import { Filter } from "Components/filter/Filter"
import { FilterSection } from "Components/filter/FilterSection"
import { DeliveryNoteFilterForm } from "Components/forms/filter/DeliveryNoteFilterForm"
import { NoSearchDataFound } from "Components/NoSearchDataFound"
import { PageBody } from "Components/page/pageBody"
import { PageTitle } from "Components/page/pageTitle"
import { SearchInput } from "Components/searchInput"
import { useDeliveryNote, useDeliveryNotes, useMutateDeliveryNotes } from "Features/pages/deliveryNotes/hooks/useDeliveryNotes.hook"
import { useDeliveryNotesTable } from "Features/pages/deliveryNotes/hooks/useDeliveryNotesTable.hook"
import { DeliveryNote, DeliveryNoteFormValues, DeliveryNoteTableColumn } from "Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { DraggableColumnMenu } from "Features/pages/purchaseOrders/components/DragableColumnMenu"
import { useDrawer } from "Features/pages/purchaseOrders/hooks/useDrawer.hook"
import { BulletPointIcon } from "Features/pages/purchaseOrders/icons/BulletPointIcon"
import { convertSortOrder } from "Shared/queries/query.utils"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { useSnapToFirstNonStickyOnResize } from "../purchaseOrders/hooks/useTableSnapOnResize.hook"
import { DeliveryNoteColumns, GenerateDeliveryNoteColumns } from "./columns/DeliveryNoteColumns"
import { DeliveryNoteDrawer } from "./components/DelieryNoteDrawer"
import { DeliveryNoteBillingBanner } from "./components/DeliveryNoteBillingBanner"
import { useDeliveryNoteFilter } from "./hooks/useDeliveryNoteFilter"

const columnSortKeyMap: Partial<Record<keyof DeliveryNoteTableColumn, Array<string>>> = {
    [DeliveryNoteColumns.NAME]: ["deliveryNoteNumber"],
    [DeliveryNoteColumns.VENDOR]: ["vendor.name"],
    [DeliveryNoteColumns.DATE]: ["dateOfIssue"],
    // TODO use single sort field (full name) https://levaroio.atlassian.net/browse/WEB-2272
    [DeliveryNoteColumns.CREATED_BY]: ["createdBy.firstName", "createdBy.lastName"],
}

export const DeliveryNotesListPage = () => {
    const { t } = useTranslation()

    const { filters, filterQuery, onFilter, onRemoveFilter, isFilterModalShowing, showFilterModal, hideFilterModal } = useDeliveryNoteFilter()

    const {
        tableData,
        isLoading,
        handleSearch,
        search,
        isNoSearchDataFound,
        isTableEmpty,
        pagination: { current: currentPage, pageSize, total: totalItems },
        setCurrentPage,
        setPageSize,
        setSortParams,
        resetSorting,
    } = useDeliveryNotes(filterQuery)

    const { deleteDeliveryNote } = useMutateDeliveryNotes()
    const { getDeliveryNote } = useDeliveryNote()

    const { isDrawerOpen, openDrawer, closeDrawer } = useDrawer<DeliveryNoteFormValues>()

    const [editingDN, setEditingDN] = React.useState<DeliveryNote>()

    const { tableProps, visibleColumns, columnOrder, handleToggleColumn, allColumns, handleReorderColumns } = useDeliveryNotesTable<DeliveryNoteTableColumn>({
        data: tableData,
        generateColumns: GenerateDeliveryNoteColumns,
        basePath: RoutePathEnum.DELIVERY_NOTE_DETAILS_PAGE,
        // Enable pagination
        enablePagination: true,
        totalItems,
        // Pass custom handlers if needed
        columnFunctions: {
            updateRecord: async (item: DeliveryNoteTableColumn) => {
                // This should trigger opening the modal
                const dnData = await getDeliveryNote(item.key)
                if (dnData) {
                    setEditingDN(dnData)
                    openDrawer()
                }
            },
            deleteRecord: (key: string) => {
                deleteDeliveryNote(key)
            },
            getRecord: (dn: DeliveryNoteTableColumn) => getDeliveryNote(dn.key),
            t,
        },
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        setSorting: ({ field, order }) => {
            if (field && order) {
                const params = columnSortKeyMap[field as keyof typeof columnSortKeyMap]!.map((sortField) => ({ sortField, sortOption: convertSortOrder(order) }))
                setSortParams(params)
            } else {
                resetSorting()
            }
        },
    })

    const scrollRef = useRef<HTMLDivElement>(null)
    useSnapToFirstNonStickyOnResize(scrollRef)

    if (isLoading) {
        return <Spin />
    }

    return (
        <>
            <DeliveryNoteBillingBanner />

            <PageTitle titleKey={"deliveryNote:list.title"}>
                <SearchInput className="min-w-32" onSearch={handleSearch} value={search} hidden={false} disabled={isTableEmpty} />
                <Filter
                    {...{
                        FilterForm: DeliveryNoteFilterForm,
                        isFilterModalShowing,
                        showFilterModal,
                        hideFilterModal,
                        filters,
                        onFilter,
                    }}
                    disabled={isTableEmpty}
                />
                <Dropdown
                    dropdownRender={() => (
                        <DraggableColumnMenu
                            {...{
                                allColumns,
                                columnOrder,
                                visibleColumns,
                                onToggleColumn: handleToggleColumn,
                                onReorderColumns: handleReorderColumns,
                                i18nColumnBasePath: "deliveryNote:columns.",
                            }}
                        />
                    )}
                    trigger={["click"]}
                    placement="bottomRight"
                    className="z-50 flex-1"
                    disabled={isTableEmpty}
                >
                    <Button icon={<BulletPointIcon />}>{t("deliveryNote:list.columns")}</Button>
                </Dropdown>
            </PageTitle>

            <FilterSection filters={filters} onRemove={onRemoveFilter} />

            <PageBody>
                {isTableEmpty ? (
                    <NoSearchDataFound title="info:delivery_note.empty_table.title" message="info:delivery_note.empty_table.text" icon="./icons/empty_table.svg">
                        <Button className="mt-16" type="primary" onClick={openDrawer}>
                            <PlusIcon />
                            <span>{t("info:delivery_note.empty_table.button")}</span>
                        </Button>
                    </NoSearchDataFound>
                ) : isNoSearchDataFound ? (
                    <NoSearchDataFound title="info:no_filter_data_found.title.delivery_note" message="info:no_filter_data_found.message_approver" />
                ) : (
                    <Table ref={scrollRef} {...tableProps} />
                )}
            </PageBody>

            <DeliveryNoteDrawer open={isDrawerOpen} onClose={closeDrawer} editMode={Boolean(editingDN)} deliveryNote={editingDN} />
        </>
    )
}
